// src/components/Header.jsx
import React, { useContext, useMemo, useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  IconButton,
  Badge,
  Button,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Link as MuiLink,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { CartContext } from "../context/CartContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Header({ onCartClick }) {
  const { cartItems } = useContext(CartContext);
  const cartCount = useMemo(
    () =>
      Array.isArray(cartItems)
        ? cartItems.reduce((n, it) => n + (it?.quantity || 0), 0)
        : 0,
    [cartItems]
  );

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [open, setOpen] = useState(false); // mobile nav drawer
  const location = useLocation();
  const { pathname, search } = location;

  const toggle = (val) => () => setOpen(val);

  // Open the cart if ?cart=1 is present (e.g., from /cancel → "/?cart=1")
  // Guard with a ref to avoid duplicate opens in React Strict Mode.
  const openedViaQueryRef = useRef(false);
  useEffect(() => {
    const shouldOpen = new URLSearchParams(search).get("cart") === "1";
    if (shouldOpen && !openedViaQueryRef.current) {
      openedViaQueryRef.current = true;
      onCartClick?.();
    }
    if (!shouldOpen && openedViaQueryRef.current) {
      // reset when param disappears so it can open again next time
      openedViaQueryRef.current = false;
    }
  }, [search, onCartClick]);

  const linkSx = {
    fontWeight: 600,
    whiteSpace: "nowrap",
    textDecoration: "none",
    color: "inherit",
    "&.active": { color: "primary.main" },
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{ backgroundColor: "#fff", color: "#111" }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ gap: 2, minHeight: 72 }}>
          {/* Left: Logo */}
          <Typography component="div" sx={{ fontWeight: 800, mr: 1 }}>
            <MuiLink
              component={RouterLink}
              to="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                lineHeight: 0,
              }}
            >
              <Box
                component="img"
                src="/images/Jam_Can_Logo-removebg.png"
                alt="JamCan Hot Pepper Sauce"
                sx={{ width: 80, height: "auto" }}
              />
            </MuiLink>
          </Typography>

          {/* Center/Right: Desktop nav */}
          {isMdUp ? (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 3, ml: "auto" }}
            >
              {NAV.map(({ label, to }) => (
                <MuiLink
                  key={to}
                  component={RouterLink}
                  to={to}
                  className={pathname === to ? "active" : ""}
                  sx={linkSx}
                >
                  {label}
                </MuiLink>
              ))}

              {/* Cart (always visible) */}
              <IconButton
                color="inherit"
                aria-label="Open cart"
                onClick={onCartClick}
                sx={{ ml: 1 }}
              >
                <Badge badgeContent={cartCount} color="error">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            </Box>
          ) : (
            // Mobile: cart + hamburger
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}
            >
              <IconButton
                color="inherit"
                aria-label="Open cart"
                onClick={onCartClick}
              >
                <Badge badgeContent={cartCount} color="error">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
              <IconButton
                color="inherit"
                aria-label="Open menu"
                onClick={toggle(true)}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={toggle(false)}
        PaperProps={{ sx: { width: 300 } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
          }}
        >
          <Box
            component="img"
            src="/images/Jam_Can_Logo-removebg.png"
            alt="JamCan Hot Pepper Sauce"
            sx={{ width: 56, height: "auto" }}
          />
          <IconButton aria-label="Close menu" onClick={toggle(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          {NAV.map(({ label, to }) => {
            const active = pathname === to;
            return (
              <ListItemButton
                key={to}
                component={RouterLink}
                to={to}
                onClick={toggle(false)}
                selected={active}
                sx={{
                  "&.Mui-selected": { backgroundColor: "action.selected" },
                }}
              >
                <ListItemText
                  primaryTypographyProps={{
                    sx: { fontWeight: 700, whiteSpace: "nowrap" },
                  }}
                  primary={label}
                />
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/products"
            onClick={toggle(false)}
            sx={{ borderRadius: 2, py: 1.25, fontWeight: 700 }}
          >
            Shop Now
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
}
